// src/utils/proctoring.js

export const Events = Object.freeze({
  START: 'START',
  STOP: 'STOP',
  CHAT: 'CHAT',
  BROADCAST: 'BROADCAST',
  SYSTEM_CHECK_SUCCESS: 'SYSTEM_CHECK_SUCCESS',
  SYSTEM_CHECK_FAILED: 'SYSTEM_CHECK_FAILED',
  CAMERA_UNAVAILABLE: 'CAMERA_UNAVAILABLE',
  SCREEN_UNAVAILABLE: 'SCREEN_UNAVAILABLE',
  MICROPHONE_UNAVAILABLE: 'MICROPHONE_UNAVAILABLE',
  INTERNET_CONNECTED: 'INTERNET_CONNECTED',
  INTERNET_DISCONNECTED: 'INTERNET_DISCONNECTED',
  PROCTORING_STOPPED: 'PROCTORING_STOPPED',
  SESSION_EXPIRED: 'SESSION_EXPIRED',
  PAUSE_TEST: 'PAUSE_TEST',
  PAUSE_CONFIRM: 'PAUSE_CONFIRM',
  RESUME_TEST: 'RESUME_TEST',
  RESUME_CONFIRM: 'RESUME_CONFIRM',
  END_TEST: 'END_TEST',
  END_CONFIRM: 'END_CONFIRM',
  AI_ALERTS: 'AI_ALERTS',
  CLOSED_PROCTORING_WINDOW: 'PROCTORING_WINDOW_CLOSED',
});

export const TestState = Object.freeze({
  NOT_STARTED: 'NOT_STARTED',
  STARTED: 'STARTED',
  PAUSED: 'PAUSED',
  ENDED: 'ENDED',
});

const ProctoringWindowStatus = Object.freeze({
  NOT_LAUNCHED: 'NOT_LAUNCHED',
  OPEN: 'OPEN',
  CLOSED: 'CLOSED',
});

const TYPE_KEY = 'type',
  DATA_KEY = 'data',
  TIME_KEY = 'time',
  METADATA_KEY = 'metadata';

let launchSuccessCallback, launchErrorCallback;
let isProctoringStoppedSent = false;
let proctoringStarted = false;
let internetStatus = Events.INTERNET_CONNECTED;
let _testState = TestState.NOT_STARTED;

function handleProctorMessage(event) {
  if (!event || !event.data) return;
  const data = event.data;

  switch (data.type) {
    case Events.SYSTEM_CHECK_SUCCESS:
      launchSuccessCallback(proctoringSession);
      isProctoringStoppedSent = false;
      break;
    case Events.SYSTEM_CHECK_FAILED:
      launchErrorCallback(data);
      break;
    case Events.INTERNET_CONNECTED:
    case Events.INTERNET_DISCONNECTED:
      internetStatus = data.type;
      proctoringSession.getEventListener()?.(data);
      break;
    case Events.CHAT:
      proctoringSession.getMessageListener()?.(data);
      break;
    case Events.BROADCAST:
      proctoringSession.getBroadcastMessageListener()?.(data);
      break;
    case Events.CLOSED_PROCTORING_WINDOW:
    case Events.PAUSE_TEST:
    case Events.RESUME_TEST:
    case Events.END_TEST:
      proctoringSession.getEventListener()?.(data);
      break;
    case Events.SESSION_EXPIRED:
    case Events.CAMERA_UNAVAILABLE:
    case Events.SCREEN_UNAVAILABLE:
    case Events.MICROPHONE_UNAVAILABLE:
      proctoringSession.getErrorListener()?.(data);
      break;
    case Events.AI_ALERTS:
      proctoringSession.getAIAlertsListener()?.(data);
      break;
    case Events.PROCTORING_STOPPED:
      if (isProctoringStoppedSent) return;
      proctoringStarted = false;
      proctoringSession.getErrorListener()?.(data);
      isProctoringStoppedSent = true;
      break;
  }
}

export const proctoringSession = new (function () {
  let eventListener, messageListener, errorListener, aiAlertListener, broadcastMessageListener;
  let candidateStoppedProctoring = false;

  function isWindowOpen() {
    const win = EP.getWindowRef();
    return win && !win.closed;
  }
  function startProctoring() {
    if (proctoringStarted) return;
    const message = {
      [TYPE_KEY]: Events.START,
      [METADATA_KEY]: { isTestPaused: _testState === TestState.PAUSED },
    };
    EP.sendProctorMessage(message);
    proctoringStarted = true;
    candidateStoppedProctoring = false;
  }

  function stopProctoring() {
    if (candidateStoppedProctoring) return;
    const message = { [TYPE_KEY]: Events.STOP };
    EP.sendProctorMessage(message);
    candidateStoppedProctoring = true;
    proctoringStarted = false;
  }

  function pauseConfirm(meta) {
    _testState = TestState.PAUSED;
    sendConfirmationToProctor(Events.PAUSE_CONFIRM, meta);
  }

  function resumeConfirm(meta) {
    _testState = TestState.STARTED;
    sendConfirmationToProctor(Events.RESUME_CONFIRM, meta);
  }

  function endConfirm(meta) {
    sendConfirmationToProctor(Events.END_CONFIRM, meta);
    candidateStoppedProctoring = true;
    proctoringStarted = false;
  }

  function sendConfirmationToProctor(messageType, meta) {
    const message = {
      [TYPE_KEY]: messageType,
      [TIME_KEY]: Date.now(),
      [METADATA_KEY]: meta?.metadata || { from: '' },
    };
    EP.sendProctorMessage(message);
  }

  return {
    start: startProctoring,
    stop: stopProctoring,
    setEventListener: (l) => (eventListener = l),
    getEventListener: () => eventListener,
    setMessageListener: (l) => (messageListener = l),
    getMessageListener: () => messageListener,
    setErrorListener: (l) => (errorListener = l),
    getErrorListener: () => errorListener,
    setAIAlertsListener: (l) => (aiAlertListener = l),
    getAIAlertsListener: () => aiAlertListener,
    setBroadcastMessageListener: (l) => (broadcastMessageListener = l),
    getBroadcastMessageListener: () => broadcastMessageListener,
    sendMessage: (chat) => {
      const message = { [TYPE_KEY]: Events.CHAT, [DATA_KEY]: chat };
      EP.sendProctorMessage(message);
    },
    pauseConfirm,
    resumeConfirm,
    endConfirm,
    isWindowOpen
  };
})();

export const EP = new (function () {
  let eoPaas;
  let epServerUrl = 'https://exam.hostexam.net';
  let epOrigin = new URL(epServerUrl).origin;

  function launch(examguid, successCallback, errorCallback, proctoringWindowLink = null) {
    if (proctoringWindowLink) epServerUrl = proctoringWindowLink;

    const params = { examguid, target: window.location.origin };
    const encryptedParams = btoa(JSON.stringify(params));

    eoPaas = window.open(
      `${epServerUrl}/account/token-verification/${encryptedParams}`,
      '_blank',
      'popup=yes,height=600,width=1000,scrollbars=yes,toolbar=0,location=0,statusbar=0,menubar=0'
    );

    launchSuccessCallback = successCallback;
    launchErrorCallback = errorCallback;
  }

  function sendProctorMessage(message) {
    if (!eoPaas || eoPaas.closed) {
      console.warn('[Proctoring] finestra non disponibile, messaggio non inviato:', message);
      return;
    }
    console.debug('[Proctoring] ->', message, 'to', epOrigin);
    eoPaas.postMessage(message, epOrigin);
  }

  return {
    launch,
    sendProctorMessage,
    getWindowRef: () => eoPaas,
  };
})();

if (typeof window !== 'undefined') {
  window.addEventListener('message', handleProctorMessage, false);
}
